import { useEffect, useMemo, useState } from 'react';
import { QuizBars } from '@mentimeter/visualizations-react/visualizations/quiz-bars';
import type { QuestionChoice } from '@mentimeter/http-clients';
import { FullSizeContainer } from '@mentimeter/visualizations-react/utils/full-size-container';
import type { QuizResult } from '@mentimeter/quiz-types';
import { useRagnar } from '@mentimeter/ragnar-react';
import { generateResult } from './utils';

interface QuizBarsDataHandlerProps {
  enableAnimations: boolean;
  showCorrect: boolean;
  choices: QuestionChoice[];
  rawResults: QuizResult[];
}

const CORRECT_REVEAL_DELAY = 1000;

export const QuizBarsDataHandler = ({
  enableAnimations,
  showCorrect,
  choices,
  rawResults,
}: QuizBarsDataHandlerProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
      fonts: { body },
    },
  } = useRagnar();
  const results = useMemo(
    () => generateResult(rawResults, choices),
    [choices, rawResults],
  );

  const [delayedShouldShowCorrect, setDelayedShouldShowCorrect] =
    useState(false);

  useEffect(() => {
    if (enableAnimations && rawResults.length > 0) {
      setDelayedShouldShowCorrect(false);
      const timer = setTimeout(
        () => setDelayedShouldShowCorrect(true),
        CORRECT_REVEAL_DELAY,
      );
      return () => clearTimeout(timer);
    } else {
      setDelayedShouldShowCorrect(showCorrect);
      return;
    }
  }, [showCorrect, enableAnimations, rawResults.length]);

  return (
    <FullSizeContainer>
      {(width, height) => (
        <QuizBars
          width={width}
          height={height}
          staticOptions={choices.map((choice) => ({
            id: choice.id,
            label: choice.label,
            isMarkedCorrect: choice.correct_answer,
          }))}
          shouldShowCorrect={delayedShouldShowCorrect}
          disableAnimations={!enableAnimations}
          data={results ?? []}
          colors={fillColors as [string, ...[string]]}
          textColor={textColor}
          fontFamily={body}
        />
      )}
    </FullSizeContainer>
  );
};
